// Typed client for the admin endpoints (all under /api/admin), consumed by the
// operator-facing /admin SPA route. Mirrors the fetch pattern in ./client.ts:
//   * every mutating call carries the CSRF gate header X-Requested-With: zjl-web
//   * credentials:'include' so the HttpOnly `admin_session` cookie rides along
//   * the BFF's `{ok}` / `{error:{code,message}}` envelope is parsed into ApiResult
// The contract here matches the shared admin contract (BFF implements / SPA
// consumes) documented in the build spec.
import { XRW_HEADER, XRW_VALUE } from "@shared/contact-contract";

export type AdminErrorCode = "AUTH" | "RATE_LIMIT" | "UNAVAILABLE" | "INTERNAL";

/** A contact submission as returned by GET /api/admin/requests. */
export interface AdminRequest {
  id: string;
  createdAt: string;
  locale: string;
  name: string;
  company: string | null;
  email: string;
  phone: string | null;
  subject: string | null;
  message: string;
  handled: boolean;
  handledAt: string | null;
}

/** Filter for GET /api/admin/requests. */
export type RequestStatus = "all" | "unhandled" | "handled";

/** Lifecycle of a tracked issue: 待处理 / 已修复 / 已关闭. */
export type IssueStatus = "open" | "fixed" | "closed";

/** Filter for GET /api/admin/issues (adds "all" on top of the lifecycle states). */
export type IssueFilter = "all" | IssueStatus;

/**
 * An image/screenshot attached to an issue (MinIO-backed). Metadata only — the
 * bytes are fetched on demand via GET /api/admin/issues/attachments/:attId.
 */
export interface IssueAttachment {
  id: string;
  filename: string;
  contentType: string;
  byteSize: number;
  /** Present on the POST response; omitted from the list payload. */
  createdAt?: string;
}

/** A tracked issue as returned by the /api/admin/issues endpoints. */
export interface AdminIssue {
  id: string;
  /** Server-generated, unique. Format: "ISS-" + 6 Crockford-base32 chars. */
  code: string;
  title: string;
  description: string | null;
  remark: string | null;
  status: IssueStatus;
  createdAt: string;
  updatedAt: string;
  /** Image/screenshot attachments (metadata only; empty when none / feature off). */
  attachments: IssueAttachment[];
}

/** A single window's distinct-visitor / pageview triple (today / last 7d / last 30d). */
export interface AnalyticsWindow {
  d1: number;
  d7: number;
  d30: number;
}

/** One country row in the last-30d geographic breakdown. */
export interface AnalyticsCountry {
  country: string;
  count: number;
  /** 0–100, one decimal place. */
  pct: number;
}

/** One city row in the last-30d geographic breakdown. */
export interface AnalyticsCity {
  country: string;
  region: string;
  city: string;
  count: number;
  pct: number;
}

/** One day in the 14-day trend, oldest-first, zero-filled. */
export interface AnalyticsTrendPoint {
  /** Asia/Shanghai business day, "YYYY-MM-DD". */
  date: string;
  pageviews: number;
  uniqueVisitors: number;
}

/**
 * The analytics overview as returned by GET /api/admin/analytics. All counts are
 * approximate (UV via HyperLogLog) and may be sparse right after launch; empty
 * data resolves to zeros / empty arrays rather than an error.
 */
export interface AdminAnalytics {
  generatedAt: string;
  uniqueVisitors: AnalyticsWindow;
  pageviews: AnalyticsWindow;
  /** Last 30d, descending, max 10. */
  topCountries: AnalyticsCountry[];
  /** Last 30d, descending, max 10. */
  topCities: AnalyticsCity[];
  /** Last 14 days, OLDEST first, one entry per day (zero-filled). */
  trend: AnalyticsTrendPoint[];
}

export interface AdminApiResult<T> {
  /** HTTP status, or 0 when the request never completed (network failure). */
  status: number;
  data: T;
  /** True when fetch itself threw (offline, DNS, CORS) — no HTTP response. */
  networkError?: boolean;
  /** Convenience: error code lifted out of the envelope when present. */
  errorCode?: AdminErrorCode;
}

interface ErrorEnvelope {
  error?: { code?: AdminErrorCode; message?: string };
}

const JSON_HEADERS: Record<string, string> = {
  "Content-Type": "application/json",
  [XRW_HEADER]: XRW_VALUE,
};

/** Shared request runner: attaches the CSRF header + cookies and parses the envelope. */
async function request<T>(
  path: string,
  init: RequestInit,
): Promise<AdminApiResult<T>> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      credentials: "include",
      headers: { ...JSON_HEADERS, ...(init.headers ?? {}) },
    });
  } catch {
    // Network-level failure: no HTTP response at all.
    return { status: 0, data: {} as T, networkError: true };
  }
  // Tolerate empty / non-JSON bodies (e.g. some error statuses).
  const data = (await res.json().catch(() => ({}))) as T;
  const errorCode = (data as ErrorEnvelope)?.error?.code;
  return { status: res.status, data, ...(errorCode ? { errorCode } : {}) };
}

// --- Endpoints ---------------------------------------------------------------

/** GET /api/admin/session — no auth required; lets the SPA pick login vs dashboard. */
export function getSession(): Promise<AdminApiResult<{ authenticated: boolean }>> {
  // GET carries no CSRF gate, but credentials:'include' still sends the cookie.
  return request<{ authenticated: boolean }>("/api/admin/session", {
    method: "GET",
  });
}

/** POST /api/admin/login — 200 {ok} sets the session cookie; 401/429/503 on failure. */
export function login(
  username: string,
  password: string,
): Promise<AdminApiResult<{ ok: true } | ErrorEnvelope>> {
  return request("/api/admin/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
}

/** POST /api/admin/logout — deletes the session + clears the cookie. */
export function logout(): Promise<AdminApiResult<{ ok: true }>> {
  return request<{ ok: true }>("/api/admin/logout", { method: "POST" });
}

/** Pagination + search params shared by both list endpoints. */
export interface ListQuery {
  page?: number;
  pageSize?: number;
  /** Free-text search (ILIKE over the relevant columns; case-insensitive). */
  q?: string;
}

/** A paginated list envelope. `total` is the full count for the current q+status. */
export interface Paged {
  total: number;
  page: number;
  pageSize: number;
}

/** Build the shared ?page&pageSize&q&status query string (omits empty q). */
function listParams(
  status: string,
  { page, pageSize, q }: ListQuery,
): string {
  const params = new URLSearchParams({ status });
  params.set("page", String(page && page > 0 ? page : 1));
  params.set("pageSize", String(pageSize && pageSize > 0 ? Math.min(pageSize, 100) : 20));
  if (q && q.trim()) params.set("q", q.trim());
  return params.toString();
}

/**
 * GET /api/admin/requests?page&pageSize&q&status — the paginated submission list
 * (requireAdmin). The "requests" key is retained for compat; `total/page/pageSize`
 * drive the pager.
 */
export function listRequests(
  status: RequestStatus,
  query: ListQuery = {},
): Promise<AdminApiResult<{ requests: AdminRequest[] } & Partial<Paged> & ErrorEnvelope>> {
  const qs = listParams(status, query);
  return request<{ requests: AdminRequest[] } & Partial<Paged> & ErrorEnvelope>(
    `/api/admin/requests?${qs}`,
    { method: "GET" },
  );
}

/** PATCH /api/admin/requests/:id — toggle the handled flag (requireAdmin). */
export function setHandled(
  id: string,
  handled: boolean,
): Promise<AdminApiResult<{ ok: true } | ErrorEnvelope>> {
  return request(`/api/admin/requests/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify({ handled }),
  });
}

/** DELETE /api/admin/requests/:id — remove a submission (requireAdmin). */
export function deleteRequest(
  id: string,
): Promise<AdminApiResult<{ ok: true } | ErrorEnvelope>> {
  return request(`/api/admin/requests/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// --- Issue tracker (all under /api/admin/issues, requireAdmin) ---------------

/**
 * GET /api/admin/issues?page&pageSize&q&status — the paginated issue list,
 * newest-first (requireAdmin). Each issue carries attachments[] (metadata only).
 */
export function listIssues(
  status: IssueFilter,
  query: ListQuery = {},
): Promise<AdminApiResult<{ issues: AdminIssue[] } & Partial<Paged> & ErrorEnvelope>> {
  const qs = listParams(status, query);
  return request<{ issues: AdminIssue[] } & Partial<Paged> & ErrorEnvelope>(
    `/api/admin/issues?${qs}`,
    { method: "GET" },
  );
}

/** Payload for POST /api/admin/issues. The server generates `code` + initial status. */
export interface CreateIssueInput {
  title: string;
  description?: string;
  remark?: string;
}

/** POST /api/admin/issues — create an issue; 201 {issue} (requireAdmin). */
export function createIssue(
  input: CreateIssueInput,
): Promise<AdminApiResult<{ issue: AdminIssue } & ErrorEnvelope>> {
  return request<{ issue: AdminIssue } & ErrorEnvelope>("/api/admin/issues", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** Payload for PATCH /api/admin/issues/:id. All fields optional (partial update). */
export interface UpdateIssueInput {
  title?: string;
  description?: string;
  remark?: string;
  status?: IssueStatus;
}

/** PATCH /api/admin/issues/:id — partial update incl. status changes (requireAdmin). */
export function updateIssue(
  id: string,
  input: UpdateIssueInput,
): Promise<AdminApiResult<{ issue: AdminIssue } & ErrorEnvelope>> {
  return request<{ issue: AdminIssue } & ErrorEnvelope>(
    `/api/admin/issues/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify(input) },
  );
}

/** DELETE /api/admin/issues/:id — remove an issue; 200 {ok}/404 (requireAdmin). */
export function deleteIssue(
  id: string,
): Promise<AdminApiResult<{ ok: true } | ErrorEnvelope>> {
  return request(`/api/admin/issues/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// --- Issue attachments (MinIO-backed images; all under requireAdmin) ---------

/** Allowed upload content-types (mirrors the BFF contract). */
export const ATTACHMENT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
] as const;
/** Per-file upload ceiling (3 MB), matched on the client before POSTing. */
export const ATTACHMENT_MAX_BYTES = 3 * 1024 * 1024;
/** Max attachments per issue (server enforces; client uses it to gate the UI). */
export const ATTACHMENT_MAX_COUNT = 5;

/**
 * POST /api/admin/issues/:id/attachments — multipart upload (field "file").
 * Carries the CSRF gate + cookies but DOES NOT set Content-Type, so the browser
 * adds the multipart boundary itself. 201 {attachment}; 404/413/415/409/503.
 */
export async function uploadIssueAttachment(
  issueId: string,
  file: File,
): Promise<AdminApiResult<{ attachment: IssueAttachment } & ErrorEnvelope>> {
  const form = new FormData();
  form.append("file", file);
  let res: Response;
  try {
    res = await fetch(
      `/api/admin/issues/${encodeURIComponent(issueId)}/attachments`,
      {
        method: "POST",
        credentials: "include",
        // NB: only the CSRF gate header — NO Content-Type (let the browser set
        // the multipart boundary).
        headers: { [XRW_HEADER]: XRW_VALUE },
        body: form,
      },
    );
  } catch {
    return {
      status: 0,
      data: {} as { attachment: IssueAttachment } & ErrorEnvelope,
      networkError: true,
    };
  }
  const data = (await res.json().catch(() => ({}))) as {
    attachment: IssueAttachment;
  } & ErrorEnvelope;
  const errorCode = (data as ErrorEnvelope)?.error?.code;
  return { status: res.status, data, ...(errorCode ? { errorCode } : {}) };
}

/**
 * GET /api/admin/issues/attachments/:attId — fetch the object bytes (authed) and
 * wrap them in an object URL for <img src>. Callers MUST revoke the returned URL
 * (URL.revokeObjectURL) when the image unmounts. Resolves to null on any failure
 * (401/404/503/network) so the caller can show a fallback.
 */
export async function fetchAttachmentObjectUrl(
  attId: string,
): Promise<{ status: number; objectUrl: string | null }> {
  let res: Response;
  try {
    res = await fetch(
      `/api/admin/issues/attachments/${encodeURIComponent(attId)}`,
      // The /api CSRF gate requires X-Requested-With on EVERY method incl. GET.
      { method: "GET", credentials: "include", headers: { [XRW_HEADER]: XRW_VALUE } },
    );
  } catch {
    return { status: 0, objectUrl: null };
  }
  if (!res.ok) return { status: res.status, objectUrl: null };
  const blob = await res.blob().catch(() => null);
  return {
    status: res.status,
    objectUrl: blob ? URL.createObjectURL(blob) : null,
  };
}

/**
 * Download the admin user-guide PDF (admin-gated GET /api/admin/guide.pdf). Fetched
 * with the CSRF header so the /api gate passes (a plain <a download> can't set it),
 * then turned into a browser download. Returns {ok,status} for error messaging.
 */
export async function downloadGuidePdf(): Promise<{ ok: boolean; status: number }> {
  let res: Response;
  try {
    res = await fetch("/api/admin/guide.pdf", {
      method: "GET",
      credentials: "include",
      headers: { [XRW_HEADER]: XRW_VALUE },
    });
  } catch {
    return { ok: false, status: 0 };
  }
  if (!res.ok) return { ok: false, status: res.status };
  const blob = await res.blob().catch(() => null);
  if (!blob) return { ok: false, status: res.status };
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "ZJL-管理后台使用说明.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { ok: true, status: res.status };
}

/** DELETE /api/admin/issues/attachments/:attId — drop the object + row; 200 {ok}. */
export function deleteIssueAttachment(
  attId: string,
): Promise<AdminApiResult<{ ok: true } | ErrorEnvelope>> {
  return request(`/api/admin/issues/attachments/${encodeURIComponent(attId)}`, {
    method: "DELETE",
  });
}

// --- Analytics overview (数据看板) -------------------------------------------

/**
 * GET /api/admin/analytics — visitor + geo overview for the dashboard tab
 * (requireAdmin). 200 {AdminAnalytics}; 401 when unauthorised, 503 when disabled,
 * mirroring the other admin reads. The payload is always well-formed (zeros /
 * empty arrays on no data), so the SPA never has to special-case "empty".
 */
export function getAnalytics(): Promise<
  AdminApiResult<AdminAnalytics & ErrorEnvelope>
> {
  return request<AdminAnalytics & ErrorEnvelope>("/api/admin/analytics", {
    method: "GET",
  });
}

// --- Login audit (登录记录) ---------------------------------------------------

/** Outcome of a recorded admin login attempt. */
export type LoginAuditOutcome = "success" | "failure" | "locked";

/** Filter for GET /api/admin/login-audit (adds "all" on top of the outcomes). */
export type LoginAuditFilter = "all" | LoginAuditOutcome;

/**
 * One recorded login attempt as returned by GET /api/admin/login-audit. The raw IP
 * is never returned — only the Cloudflare geo captured at the time (country/region/
 * city are null when the request didn't transit Cloudflare, i.e. geo isn't trusted).
 */
export interface LoginAuditEvent {
  id: string;
  createdAt: string | null;
  outcome: LoginAuditOutcome;
  username: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  userAgent: string | null;
}

/** Query for the login-audit list. `country` + `exclude` answer "logins NOT from X". */
export interface LoginAuditQuery {
  outcome?: LoginAuditFilter;
  /** ISO 3166-1 alpha-2 (e.g. "US"); ignored unless exactly two letters. */
  country?: string;
  /** When true with `country`, returns logins whose country is NOT that code. */
  exclude?: boolean;
  page?: number;
  pageSize?: number;
}

/**
 * GET /api/admin/login-audit?outcome&country&exclude&page&pageSize — paginated,
 * newest-first admin login attempts (requireAdmin). 200 {events,total,page,pageSize};
 * 401 unauthorised / 503 disabled, like the other admin reads. A partial/invalid
 * country code is dropped client-side so it never 400s while the operator types.
 */
export function listLoginAudit(
  query: LoginAuditQuery = {},
): Promise<AdminApiResult<{ events: LoginAuditEvent[] } & Partial<Paged> & ErrorEnvelope>> {
  const params = new URLSearchParams();
  if (query.outcome && query.outcome !== "all") params.set("outcome", query.outcome);
  const c = query.country?.trim();
  if (c && /^[a-z]{2}$/i.test(c)) {
    params.set("country", c.toUpperCase());
    if (query.exclude) params.set("exclude", "true");
  }
  params.set("page", String(query.page && query.page > 0 ? query.page : 1));
  params.set("pageSize", String(query.pageSize && query.pageSize > 0 ? Math.min(query.pageSize, 100) : 20));
  return request<{ events: LoginAuditEvent[] } & Partial<Paged> & ErrorEnvelope>(
    `/api/admin/login-audit?${params.toString()}`,
    { method: "GET" },
  );
}
